import { isCommentRequiredFor } from '@/types/workflowActions';
import { useContext, useEffect, useRef, useState, useMemo, useCallback, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { ColumnFiltersState, PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';
import {
  Download,
  Loader2,
  ClipboardList,
  Inbox,
  SearchX,
  List,
  LayoutGrid,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { SegmentedToggle } from '@/components/ui/segmented-toggle';
import { QueryErrorState } from '@/components/ui/query-error-state';
import { PageContainer } from '@/components/layout/PageContainer';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/table/EmptyState';
import { debtorLabel, debtorDetailHref } from '@/components/billing/debtorDisplay';
import type { FilterConfig } from '@/components/table/types';
import { BaseEditableTable } from '@/components/table/BaseEditableTable';
import type { EditableCellConfig } from '@/components/table/types';
import { AvatarDetailsButton } from '@/components/ui/avatar-details-button';
import { HelpTip } from '@/components/ui/help-tip';
import { useAuthContext } from '@/contexts/AuthContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useWorkflowItems, useWorkflowItem, useWorkflowStats } from '@/hooks/useWorkflows';
import { fetchWorkflowItems } from '@/hooks/workflowItemsQuery';
import { useEmployeePickerNames } from '@/hooks/useEmployees';
import { useUrlTab } from '@/hooks/useUrlTab';
import { formatDateDisplay, formatDateTime } from '@/lib/format';
import type { WorkflowItemListParams, WorkflowItemResponse, WorkflowSort } from '@/types/workflow';
import {
  escalationTargetLabel,
  responsibleFor,
  responsibleRuleLines,
  workflowItemPermissions,
  type ResponsibleContext,
} from '@/types/workflowResponsible';
import {
  parseChangeRequestPayload,
  classifyChangeRequestTriage,
  getChangeRequestTypeLabel,
  getEmployeeDataSectionLabel,
  formatCorrectionDelta,
  isEmployeeFiledFamily,
  parseTimeCorrectionBody,
  getFamilyLabel,
  getTriageBucketLabel,
  getSourceLabel,
  formatConfidence,
} from '@/types/changeRequest';
import {
  getCategoryLabel,
  getStateLabel,
  getStateTone,
  getUrgency,
  getCategoryColorClass,
  payloadSummary,
  isCareAlertCategory,
} from '@/types/workflow';
import { WorkflowStatsStrip } from '@/components/workflows/WorkflowStatsStrip';
import { WorkflowBoard } from '@/components/workflows/WorkflowBoard';
import {
  WorkflowBulkAllButton,
  WorkflowSelectionActions,
  type WorkflowBulkMode,
} from '@/components/workflows/WorkflowBulkActions';
import { useBatchAcknowledgeFlow } from '@/components/workflows/useBatchAcknowledgeFlow';
import {
  applyInboxClientFilters,
  chipDateRange,
  isBulkAcknowledgeable,
  responsibleFilterOptions,
  responsibleKey,
  type InboxClientFilters,
} from '@/components/workflows/inboxListFilters';
import { WorkflowDetailSheet } from '@/components/workflows/WorkflowDetailSheet';
import { ResponsibleCell } from '@/components/workflows/ResponsibleCell';
import { AssignDialog, type AssignDialogItem } from '@/components/workflows/AssignDialog';
import { RemindDialog, type RemindDialogItem } from '@/components/workflows/RemindDialog';
import { TransitionDialog } from '@/components/workflows/TransitionDialog';
import { WorkflowRowActionsMenu } from '@/components/workflows/WorkflowRowActionsMenu';
import { transitionDialogCopy } from '@/components/workflows/transitionCopy';
import { coverageBreachSummary } from '@/types/coverageBreach';
import { AccidentSeverityBadge } from '@/components/workflows/AccidentSeverityBadge';
import { BatchLockHint } from '@/components/workflows/BatchLockHint';
import { batchLockReason, isInBatch, useBatchApproveFlow } from '@/components/workflows/useBatchApproveFlow';
import { DunningWorkflowDrawer } from '@/components/dunning/DunningWorkflowDrawer';
import { DunningBlockerBadge } from '@/components/dunning/DunningBlockerBadge';
import { useDunningWorkflowDrawer } from '@/contexts/DunningWorkflowDrawerContext';
import { NameMapWithFallback } from '@/lib/nameMapWithFallback';
import { useWorkflowDetailGate } from '@/hooks/useWorkflowDetailGate';
import { ModuleUnavailableHint } from '@/components/auth/ModuleUnavailableHint';
import { AccessContext } from '@/contexts/AccessContext';
import { subsidyReportSummary } from '@/types/subsidyReport';
import { subsidyExpirySummary } from '@/types/subsidyExpiry';
import { lateExtrasSummary } from '@/types/lateExtras';
import { CARE_ALERT_SUMMARY } from '@/types/careAlertSummary';
import { SubsidyReportExportDialog } from '@/components/workflows/SubsidyReportExportDialog';

// Extended type to ensure id is always present for BaseEditableTable
// `responsible` is the row's Responsible filter value (inboxListFilters.responsibleKey).
type WorkflowTableRow = WorkflowItemResponse & { id: string; responsible: string };

const TABS = ['open', 'mine', 'overdue', 'change_requests', 'done'] as const;
type InboxTab = (typeof TABS)[number];
// Tab values of earlier links; `all` already falls back to the default.
const TAB_ALIASES: Partial<Record<string, InboxTab>> = { myTasks: 'mine' };
const VIEWS = ['list', 'board'] as const;
type WorkflowViewMode = (typeof VIEWS)[number];
const BOARD_GROUPINGS = ['person', 'status'] as const;
type BoardGrouping = (typeof BOARD_GROUPINGS)[number];
const WHO_FILTERS = ['all', 'nobody'] as const;
type WhoFilter = (typeof WHO_FILTERS)[number];

// The server filters every tab across all pages; the client never narrows a page.
const TAB_QUERY: Record<InboxTab, WorkflowItemListParams> = {
  open: { pending: true },
  mine: { pending: true, mine: true },
  overdue: { overdue: true },
  change_requests: { category: 'change_request' },
  done: { completed: true },
};

// Fixed column content widths: the visible set (details 90, category 130,
// summary 190, responsible 180, due 120, state 90, actions 48) sums to 848 px,
// plus 16 px cell padding each, 960 px, plus the table's standard checkbox
// column: it fits a 1440 px screen next to the sidebar with room for longer
// German badges. Cells wrap or truncate inside them instead of
// widening the table.
const COLUMN_WIDTH = {
  avatar: 90,
  category: 130,
  summary: 190,
  responsible: 180,
  group: 110,
  due: 120,
  created: 100,
  state: 125,
  severity: 110,
  actions: 48,
} as const;
const widthOf = (px: number) => ({ size: px, width: px });

// Created and Severity wait in the column picker, so Due, State and the
// actions fit a laptop screen without sideways scrolling. The change-request
// list keeps Severity: it carries the triage bucket there.
// Group waits there too: the Responsible cell names a lead's group and the
// detail sheet shows it.
const DEFAULT_COLUMN_VISIBILITY = { created_at: false, severity: false, group_name: false };
const CHANGE_REQUEST_COLUMN_VISIBILITY = { created_at: false, group_name: false };

// Work lists read soonest deadline first; the record lists newest first.
const DUE_FIRST: SortingState = [{ id: 'due_at', desc: false }];
const NEWEST_FIRST: SortingState = [{ id: 'created_at', desc: true }];
const defaultSorting = (tab: InboxTab): SortingState =>
  tab === 'done' || tab === 'change_requests' ? NEWEST_FIRST : DUE_FIRST;

/** The string values of one table filter chip. */
function chipValues(filters: ColumnFiltersState, id: string): string[] {
  const value = filters.find((filter) => filter.id === id)?.value;
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** The list endpoint's sort for a table sort; newest first is the server default. */
function serverSort(sorting: SortingState): WorkflowSort | undefined {
  const first = sorting[0];
  if (first?.id === 'due_at') return first.desc ? 'due_desc' : 'due_asc';
  if (first?.id === 'created_at' && !first.desc) return 'created_asc';
  return undefined;
}

export function WorkflowInboxPage() {
  const { i18n } = useLingui();
  const { userId } = useAuthContext();
  const isAdmin = useIsAdmin();
  const dunningDrawer = useDunningWorkflowDrawer();
  const canOpenDetail = useWorkflowDetailGate();
  // The Sozialdepartement export (#2003) is admin-only, like the category.
  // Outside an AccessProvider (isolated tests) the gate is open.
  const access = useContext(AccessContext);
  const canExportSubsidyReports = access ? access.isAdmin : true;
  const [subsidyExportOpen, setSubsidyExportOpen] = useState(false);

  // Tab, view, board grouping and the "nobody yet" filter live in the URL, so
  // a reload, Back and a shared link keep them.
  const [urlTab] = useUrlTab<InboxTab>('tab', TABS, 'open');
  const [viewMode, setViewMode] = useUrlTab<WorkflowViewMode>('view', VIEWS, 'list');
  const [boardGrouping, setBoardGrouping] = useUrlTab<BoardGrouping>('by', BOARD_GROUPINGS, 'person');
  const [whoFilter, setWhoFilter] = useUrlTab<WhoFilter>('who', WHO_FILTERS, 'all');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = TAB_ALIASES[searchParams.get('tab') ?? ''] ?? urlTab;
  const nobodyOnly = whoFilter === 'nobody';

  // Pagination
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  // The table reports a new page or a new page size (its "Rows per page", or the
  // size it remembers for this table). A new size starts at the first page.
  const handlePaginationChange = useCallback(
    (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
      const current = { pageIndex: Math.floor(offset / limit), pageSize: limit };
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (next.pageSize !== limit) {
        setLimit(next.pageSize);
        setOffset(0);
        return;
      }
      setOffset(next.pageIndex * next.pageSize);
    },
    [offset, limit],
  );

  // The table's search text, reported 300 ms after the last keystroke. It goes
  // to the server as `q`, so it holds across pages. The board has no search box
  // and ignores it.
  const [search, setSearch] = useState('');

  // Stats filter (clicking a stat card)
  const [statsFilter, setStatsFilter] = useState<string | null>(null);

  // The list view's Category and State chips. They seed the table again when
  // it re-mounts (back from the board), so chips and query never disagree.
  const [chipFilters, setChipFilters] = useState<ColumnFiltersState>([]);
  // The change_request tab fixes the category; its chip is hidden and ignored there.
  const chipCategories = useMemo(
    () => (viewMode === 'list' && activeTab !== 'change_requests' ? chipValues(chipFilters, 'category') : []),
    [chipFilters, viewMode, activeTab],
  );
  const chipStates = useMemo(
    () => (viewMode === 'list' ? chipValues(chipFilters, 'current_state') : []),
    [chipFilters, viewMode],
  );
  // Responsible and Due filter on the client (inboxListFilters).
  const chipResponsible = useMemo(
    () => (viewMode === 'list' ? chipValues(chipFilters, 'responsible') : []),
    [chipFilters, viewMode],
  );
  const chipDue = useMemo(
    () => (viewMode === 'list' ? chipDateRange(chipFilters, 'due_at') : null),
    [chipFilters, viewMode],
  );

  // Table sort; null follows the tab's default.
  const [sortingOverride, setSortingOverride] = useState<SortingState | null>(null);
  const sorting = sortingOverride ?? defaultSorting(activeTab);

  // The element that opened the current dialog (a row menu, a card, the detail
  // sheet): the dialog gives it the focus back when it closes.
  const [dialogOrigin, setDialogOrigin] = useState<HTMLElement | null>(null);
  const rememberDialogOrigin = useCallback(() => {
    setDialogOrigin(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  }, []);

  // Hand-over dialog: the item stays set while the dialog closes.
  const [assignItem, setAssignItem] = useState<AssignDialogItem | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const openAssign = useCallback(
    (item: AssignDialogItem) => {
      rememberDialogOrigin();
      setAssignItem(item);
      setAssignOpen(true);
    },
    [rememberDialogOrigin],
  );

  // Reminder dialog: same shape as the hand-over dialog.
  const [remindItem, setRemindItem] = useState<RemindDialogItem | null>(null);
  const [remindOpen, setRemindOpen] = useState(false);
  const openRemind = useCallback(
    (item: RemindDialogItem) => {
      rememberDialogOrigin();
      setRemindItem(item);
      setRemindOpen(true);
    },
    [rememberDialogOrigin],
  );

  // List-view batch-select (change_request scope only): the set of pending
  // change_request rows the operator has ticked for "Approve selected".
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // The table's standard selection, controlled from `selectedIds`.
  const selectedRows: RowSelectionState = useMemo(
    () => Object.fromEntries([...selectedIds].map((id) => [id, true])),
    [selectedIds],
  );
  const handleSelectionChange = useCallback((next: RowSelectionState) => {
    setSelectedIds(new Set(Object.keys(next).filter((id) => next[id])));
  }, []);

  // Detail sheet
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Transition dialog
  const [transitionDialogOpen, setTransitionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [transitionItemId, setTransitionItemId] = useState<string | null>(null);
  const [transitionCommentRequired, setTransitionCommentRequired] = useState(false);
  const [transitionCurrentState, setTransitionCurrentState] = useState<string>('');
  const [transitionReasonRequired, setTransitionReasonRequired] = useState(false);
  const [transitionCategory, setTransitionCategory] = useState<string | undefined>(undefined);

  // Stats
  const stats = useWorkflowStats();

  // Query params: the tab's server filter, the stat card and the "nobody yet"
  // toggle, the sort, and a page or the whole set.
  const queryParams = useMemo((): WorkflowItemListParams => {
    const params: WorkflowItemListParams = { ...TAB_QUERY[activeTab] };
    if (statsFilter === 'dueToday') params.dueToday = true;
    if (nobodyOnly) params.unassigned = true;
    const sort = serverSort(sorting);
    if (sort) params.sort = sort;
    // The endpoint matches one category and one state; a chip with one value
    // filters on the server.
    if (chipCategories.length === 1) params.category = chipCategories[0];
    if (chipStates.length === 1) params.state = chipStates[0];
    const q = search.trim();
    if (q && viewMode === 'list') params.q = q;
    // The board groups client-side, and the change_request scope groups
    // client-side too: both need the COMPLETE set, not a server page, so the
    // hook paginates to completion (fetchAll) instead of capping rows. A chip
    // with several values, a Responsible or a Due chip filters the complete set
    // client-side for the same reason.
    const clientSideChip =
      chipCategories.length > 1 || chipStates.length > 1 || chipResponsible.length > 0 || chipDue !== null;
    if (viewMode === 'board' || activeTab === 'change_requests' || clientSideChip) {
      return { ...params, fetchAll: true };
    }
    return { ...params, limit, offset };
  }, [
    activeTab,
    statsFilter,
    nobodyOnly,
    sorting,
    chipCategories,
    chipStates,
    chipResponsible,
    chipDue,
    search,
    viewMode,
    limit,
    offset,
  ]);

  // Clerk user id -> name from the any-module employee picker (#1688), so every
  // role that reads the inbox names the actors. An actor the picker cannot
  // resolve (an admin without an employee record, a deleted employee) renders
  // "Unknown", never the raw Clerk id.
  const { namesByClerkUserId } = useEmployeePickerNames();
  const unknownActor = i18n._({ id: 'common.unknown', message: 'Unknown' });
  const userNameMap = useMemo(() => {
    const map = new NameMapWithFallback(unknownActor);
    for (const [clerkUserId, name] of namesByClerkUserId) map.set(clerkUserId, name);
    // Add current user as fallback if not already in map
    if (userId && !map.has(userId)) {
      map.set(userId, i18n._({ id: 'workflow.user.you', message: 'You' }));
    }
    return map;
  }, [namesByClerkUserId, userId, i18n, unknownActor]);
  const responsibleContext: ResponsibleContext = useMemo(
    () => ({ userId, isAdmin, namesByClerkUserId, unknownName: unknownActor }),
    [userId, isAdmin, namesByClerkUserId, unknownActor],
  );

  const { data, isLoading, error, refetch } = useWorkflowItems(queryParams);
  const {
    data: detailData,
    error: detailError,
    refetch: refetchDetail,
  } = useWorkflowItem(selectedItemId ?? '', {
    enabled: !!selectedItemId,
  });

  const items: WorkflowTableRow[] = useMemo(
    () =>
      (data?.items ?? []).map((item) => ({
        ...item,
        id: item.id ?? '',
        responsible: responsibleKey(responsibleFor(item, responsibleContext)),
      })),
    [data, responsibleContext],
  );
  // Every row loaded so far, by id: a selection kept across server pages still
  // knows the category and the actions of rows that are off the page now.
  const seenRows = useRef(new Map<string, WorkflowTableRow>());
  useEffect(() => {
    for (const item of items) seenRows.current.set(item.id, item);
  }, [items]);

  // The server filters every tab; the change_request scope keeps a client
  // check so a stray category never lands in the triage buckets. The chips
  // filter client-side too: a no-op after a one-value server filter, the
  // filter itself when a chip has several values. Responsible and Due always
  // filter here.
  const clientFilters: InboxClientFilters = useMemo(
    () => ({
      changeRequestsOnly: activeTab === 'change_requests',
      categories: chipCategories,
      states: chipStates,
      responsibleKeys: chipResponsible,
      due: chipDue,
    }),
    [activeTab, chipCategories, chipStates, chipResponsible, chipDue],
  );
  const filteredItems = useMemo(
    () => applyInboxClientFilters(items, clientFilters, responsibleContext),
    [items, clientFilters, responsibleContext],
  );

  const totalCount = queryParams.fetchAll ? filteredItems.length : (data?.total_count ?? 0);

  // A refetch can leave the current page empty while earlier pages still hold
  // rows (the last item on the last page was acknowledged or approved). Step
  // back to the last page with rows once the new data is in.
  const settled = !!data && !isLoading;
  useEffect(() => {
    if (!settled || viewMode !== 'list' || offset === 0 || totalCount === 0 || offset < totalCount) return;
    setOffset(Math.floor((totalCount - 1) / limit) * limit);
  }, [settled, viewMode, offset, totalCount, limit]);


  const handleOpenDetail = useCallback(
    (itemId: string) => {
      // Find the item to check its category
      const item = items.find((i) => i.id === itemId);
      if (!canOpenDetail(item?.category)) return;
      if (item?.category === 'dunning') {
        dunningDrawer?.openDrawerForWorkflow(itemId);
        return;
      }
      setSelectedItemId(itemId);
      setSheetOpen(true);
    },
    [items, dunningDrawer, canOpenDetail],
  );

  // The item comes from the caller (the detail sheet passes it), else the page,
  // else the open detail: an item opened from a related link or a toast is not
  // on the page, and its category and comment rule must still apply.
  const handleActionClick = useCallback(
    (itemId: string, action: string, currentState: string, source?: WorkflowItemResponse) => {
      const item =
        source ?? items.find((i) => i.id === itemId) ?? (detailData?.id === itemId ? detailData : undefined);
      // employee_data (#1766), time_correction (#1979): a reject must carry a
      // reason the employee sees -- the same rule ChangeRequestPayload applies on
      // the detail-sheet path.
      setTransitionReasonRequired(
        item?.category === 'change_request' &&
          isEmployeeFiledFamily(parseChangeRequestPayload(item.payload)?.family),
      );
      rememberDialogOrigin();
      setTransitionItemId(itemId);
      setTransitionCategory(item?.category);
      setTransitionCommentRequired(isCommentRequiredFor(item, action));
      setSelectedAction(action);
      setTransitionCurrentState(currentState);
      setTransitionDialogOpen(true);
    },
    [items, detailData, rememberDialogOrigin],
  );

  // "Approve all" / "Approve selected": previews first, one confirm with the
  // list, names on failure. "Show" in the failure toast opens the first item.
  const showItem = useCallback(
    (itemId: string) => {
      if (!canOpenDetail('change_request')) return;
      setSelectedItemId(itemId);
      setSheetOpen(true);
    },
    [canOpenDetail],
  );
  const batchFlow = useBatchApproveFlow({ onShowItem: showItem });
  const { heldBack } = batchFlow;
  const openBatch = batchFlow.open;
  const handleBatchApprove = useCallback(
    (batchItems: (WorkflowItemResponse & { id: string })[], fromSelection = false) => {
      if (batchItems.length === 0) return;
      setSelectedIds(new Set());
      openBatch(batchItems, { fromSelection });
    },
    [openBatch],
  );

  // "Acknowledge selected / all" for notice items (Development Talk Due and the like).
  const ackFlow = useBatchAcknowledgeFlow();
  const openAck = ackFlow.open;
  const handleAcknowledgeSelected = useCallback(
    (ticked: WorkflowTableRow[]) => {
      setSelectedIds(new Set());
      openAck(ticked, { fromSelection: true });
    },
    [openAck],
  );
  // "All" means every page of the current view, read fresh with the same
  // filters, not just the page on screen.
  const handleAcknowledgeAll = useCallback(() => {
    setSelectedIds(new Set());
    const { limit: _limit, offset: _offset, ...viewParams } = queryParams;
    openAck(async () => {
      const result = await fetchWorkflowItems({ ...viewParams, fetchAll: true });
      const rows = (result.items ?? []).map((item) => ({
        ...item,
        id: item.id ?? '',
        responsible: responsibleKey(responsibleFor(item, responsibleContext)),
      }));
      return applyInboxClientFilters(rows, clientFilters, responsibleContext);
    });
  }, [openAck, queryParams, clientFilters, responsibleContext]);

  const handleTransitionSuccess = useCallback(() => {
    setTransitionDialogOpen(false);
    setSelectedAction(null);
    setTransitionItemId(null);
    setTransitionReasonRequired(false);
  }, []);

  // The view before entering the change-request tab; null when the URL had none.
  const viewBeforeChangeRequests = useRef<string | null>(null);

  // One URL update for tab, view and filter: two setters in a row would each
  // start from the same old params and the second would drop the first.
  const writeTab = useCallback(
    (tab: InboxTab, options: { clearWho?: boolean } = {}) => {
      setSearchParams(
        (previous) => {
          const params = new URLSearchParams(previous);
          const wasChangeRequests = previous.get('tab') === 'change_requests';
          if (tab === 'open') params.delete('tab');
          else params.set('tab', tab);
          if (options.clearWho) params.delete('who');
          // The change_request triage worklist is the two-bucket board — default to it
          // when entering the scope so the headline UX is what the operator lands on.
          // Leaving the scope gives the other tabs back the view they had.
          if (tab === 'change_requests' && !wasChangeRequests) {
            viewBeforeChangeRequests.current = previous.get('view');
            params.set('view', 'board');
          } else if (tab !== 'change_requests' && wasChangeRequests) {
            const before = viewBeforeChangeRequests.current;
            if (before) params.set('view', before);
            else params.delete('view');
            viewBeforeChangeRequests.current = null;
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetPaging = useCallback(() => {
    setOffset(0);
    setSortingOverride(null);
    setSelectedIds(new Set());
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setOffset(0);
    setSelectedIds(new Set());
  }, []);

  const handleStatsFilterClick = useCallback(
    (filter: string) => {
      resetPaging();
      if (!filter) {
        setStatsFilter(null);
        writeTab('open');
        return;
      }
      setStatsFilter(filter);
      if (filter === 'overdue') {
        writeTab('overdue');
      } else if (filter === 'myTasks') {
        writeTab('mine');
      } else {
        // pending, and dueToday (a filter on top of the open tab)
        writeTab('open');
      }
    },
    [writeTab, resetPaging],
  );

  const handleTabChange = useCallback(
    (tab: InboxTab) => {
      writeTab(tab);
      setStatsFilter(null);
      resetPaging();
    },
    [writeTab, resetPaging],
  );

  // "Show all items": back to the open tab without any narrowing.
  const handleShowAll = useCallback(() => {
    writeTab('open', { clearWho: true });
    setStatsFilter(null);
    setSearch('');
    resetPaging();
  }, [writeTab, resetPaging]);

  const handleNobodyToggle = useCallback(() => {
    setWhoFilter(nobodyOnly ? 'all' : 'nobody');
    setOffset(0);
    setSelectedIds(new Set());
  }, [nobodyOnly, setWhoFilter]);

  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    setChipFilters(filters);
    setOffset(0);
    setSelectedIds(new Set());
  }, []);

  // Only the deadline and the creation time sort on the server; sorting any
  // other column would order one page and mislead.
  const handleSortingChange = useCallback(
    (next: SortingState) => {
      const serverSortable = next.filter((s) => s.id === 'due_at' || s.id === 'created_at');
      setSortingOverride(serverSortable.length > 0 ? serverSortable.slice(0, 1) : null);
      setOffset(0);
    },
    [],
  );

  const handleCellUpdate = useCallback(async () => {}, []);

  // Filter chips. Category and State with one value filter on the server;
  // Responsible and Due filter the complete view on the client. Group has no
  // filter, so it has no chip.
  const filterConfig: FilterConfig<WorkflowTableRow> = useMemo(
    () =>
      activeTab === 'change_requests'
        ? { current_state: 'select' as const, responsible: 'select' as const, due_at: 'date' as const }
        : {
            category: 'select' as const,
            current_state: 'select' as const,
            responsible: 'select' as const,
            due_at: 'date' as const,
          },
    [activeTab],
  );

  // The chip options are the values on the loaded page, labelled like the
  // columns print them (never the raw key).
  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(items.map((item) => item.category ?? '').filter(Boolean)))
        .map((value) => ({ value, label: getCategoryLabel(i18n, value) }))
        .sort((a, b) => a.label.localeCompare(b.label, i18n.locale)),
    [items, i18n],
  );
  const stateOptions = useMemo(
    () =>
      Array.from(new Set(items.map((item) => item.current_state ?? '').filter(Boolean)))
        .map((value) => ({ value, label: getStateLabel(i18n, value) }))
        .sort((a, b) => a.label.localeCompare(b.label, i18n.locale)),
    [items, i18n],
  );

  // Role queues, "Nobody yet" and every person, so a person with no item on
  // the loaded page can still be picked.
  const responsibleOptions = useMemo(
    () => responsibleFilterOptions(i18n, items, responsibleContext),
    [i18n, items, responsibleContext],
  );

  // Column definitions
  const columns: EditableCellConfig<WorkflowTableRow>[] = useMemo(
    () => {
      // Severity is hidden by default outside the change-request list; the
      // accident severity and the dunning blocker still show, after the summary.
      const withSummaryBadge = (r: WorkflowTableRow, content: ReactNode) => {
        if (activeTab === 'change_requests') return content;
        const payload = r.payload as Record<string, unknown> | undefined;
        const blocker = r.category === 'dunning' ? (payload?.blocker as { reason: string } | undefined) : undefined;
        const severity = r.category === 'accident_notification' ? (payload?.severity as string | undefined) : undefined;
        if (!blocker && !severity) return content;
        // The name keeps the first line; the badge wraps under it when both do
        // not fit, and shrinks before it cuts the name.
        return (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 max-w-[190px]">
            <div className="min-w-0 max-w-full">{content}</div>
            <span className="min-w-0 max-w-full" data-testid={`summary-badge-${r.id}`}>
              {blocker ? <DunningBlockerBadge reason={blocker.reason} /> : <AccidentSeverityBadge severity={severity} />}
            </span>
          </div>
        );
      };
      return [
      {
        accessorKey: 'id' as keyof WorkflowTableRow,
        id: 'avatar',
        ...widthOf(COLUMN_WIDTH.avatar),
        header: '',
        editable: false,
        sortable: false,
        enableHiding: false,
        cell: ({ row }) => {
          const payload = row.original.payload as Record<string, unknown> | undefined;
          const summary = payloadSummary(payload);
          const categoryColorClass = getCategoryColorClass(row.original.category);
          const categoryIcon = <ClipboardList className="h-4 w-4" aria-hidden="true" />;
          if (!canOpenDetail(row.original.category)) {
            // The row stays; its detail reads a module the role lacks (#1691).
            return (
              <div className="flex items-center gap-2">
                <AvatarDetailsButton
                  firstName={getCategoryLabel(i18n, row.original.category ?? '')}
                  lastName=""
                  icon={categoryIcon}
                  iconClassName={categoryColorClass}
                  title={summary}
                  nonClickable
                  hideDetailsLabel
                />
                <ModuleUnavailableHint variant="inline" />
              </div>
            );
          }
          return (
            <AvatarDetailsButton
              firstName={getCategoryLabel(i18n, row.original.category ?? '')}
              lastName=""
              icon={categoryIcon}
              iconClassName={categoryColorClass}
              title={summary}
              onClick={() => handleOpenDetail(row.original.id)}
            />
          );
        },
      },
      {
        accessorKey: 'category' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.category', message: 'Category' }),
        ...widthOf(COLUMN_WIDTH.category),
        sortable: false,
        editable: false,
        selectOptions: categoryOptions,
        cell: ({ row }) => {
          const label = getCategoryLabel(i18n, row.original.category ?? '');
          return (
            // Two lines, never cut: a long label stays readable in German.
            <span className="text-sm whitespace-normal break-words line-clamp-2 block" title={label}>
              {label}
            </span>
          );
        },
      },
      {
        accessorKey: 'payload' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.summary', message: 'Summary' }),
        id: 'summary',
        ...widthOf(COLUMN_WIDTH.summary),
        editable: false,
        sortable: false,
        cell: ({ row }) => {
          if (row.original.category === 'dunning') {
            const debtor = row.original.debtor_account;
            if (debtor?.id) {
              const label = debtorLabel(debtor, i18n);
              return withSummaryBadge(
                row.original,
                <Link
                  to={debtorDetailHref(debtor.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm truncate max-w-[190px] block text-primary hover:underline"
                  title={label}
                >
                  {label}
                </Link>,
              );
            }
            const fallback = row.original.reference_label ?? undefined;
            return withSummaryBadge(
              row.original,
              <span className="text-sm truncate max-w-[190px] block" title={fallback}>
                {fallback ?? '—'}
              </span>,
            );
          }
          if (row.original.category === 'change_request') {
            const view = parseChangeRequestPayload(row.original.payload);
            // employee_data (#1766): family and section only -- never a field
            // value (an address or phone number) in the list.
            // time_correction (#1979): the day and the difference.
            if (view?.family === 'time_correction') {
              const body = parseTimeCorrectionBody(view.executor?.body);
              const correctionLabel = body
                ? i18n._({
                    id: 'changeRequest.timeCorrection.summary',
                    message: '{family}: {date}, {delta}',
                    values: {
                      family: getFamilyLabel(i18n, 'time_correction'),
                      date: formatDateDisplay(body.date),
                      delta: formatCorrectionDelta(body.deltaMinutes),
                    },
                  })
                : getFamilyLabel(i18n, 'time_correction');
              return (
                <span className="text-sm truncate max-w-[190px] block" title={correctionLabel}>
                  {correctionLabel}
                </span>
              );
            }
            if (view?.family === 'employee_data') {
              const employeeLabel = i18n._({
                id: 'changeRequest.employeeData.summary',
                message: '{family}: {section}',
                values: {
                  family: getFamilyLabel(i18n, 'employee_data'),
                  section: getEmployeeDataSectionLabel(i18n, view.sub_type),
                },
              });
              return (
                <span className="text-sm truncate max-w-[190px] block" title={employeeLabel}>
                  {employeeLabel}
                </span>
              );
            }
            const commentValue =
              (view?.executor?.body?.comment as { value?: string } | undefined)?.value;
            const typeLabel = getChangeRequestTypeLabel(i18n, view);
            const label = commentValue
              ? i18n._({
                  id: 'workflow.summary.typeWithComment',
                  message: '{type}: {comment}',
                  values: { type: typeLabel, comment: commentValue },
                })
              : typeLabel;
            return (
              <span className="text-sm truncate max-w-[190px] block" title={label}>
                {label}
              </span>
            );
          }
          const payload = row.original.payload as Record<string, unknown> | undefined;
          const summary =
            row.original.category === 'subsidy_report'
              ? subsidyReportSummary(i18n, payload)
              : row.original.category === 'subsidy_expiry'
                ? subsidyExpirySummary(i18n, payload)
                : row.original.category === 'late_extras'
                  ? lateExtrasSummary(i18n, payload)
                  : row.original.category === 'coverage_breach'
                    ? (coverageBreachSummary(i18n, payload, i18n.locale) ?? payloadSummary(payload))
                    : isCareAlertCategory(row.original.category)
                      ? CARE_ALERT_SUMMARY[row.original.category](i18n, payload)
                      : payloadSummary(payload);
          return withSummaryBadge(
            row.original,
            <span className="text-sm truncate max-w-[190px] block" title={summary}>
              {summary ?? '—'}
            </span>,
          );
        },
      },
      {
        accessorKey: 'responsible' as keyof WorkflowTableRow,
        header: (
          <span className="inline-flex items-center gap-1">
            {i18n._({ id: 'workflow.column.responsible', message: 'Responsible' })}
            <HelpTip
              content={responsibleRuleLines(i18n).join('\n')}
              guideTopic="inbox.approvals"
              data-testid="responsible-help"
            />
          </span>
        ),
        ...widthOf(COLUMN_WIDTH.responsible),
        editable: false,
        sortable: false,
        selectOptions: responsibleOptions,
        cell: ({ row }) => {
          const r = row.original;
          // The escalation target rides on the overdue row (issue #1297); the
          // backend sets it for overdue, open items only.
          const overdue = getUrgency(r.due_at) === 'overdue' && !r.completed_at;
          return (
            <ResponsibleCell
              responsible={responsibleFor(r, responsibleContext)}
              escalationTargetName={overdue ? escalationTargetLabel(i18n, r) : undefined}
              escalationTestId={`escalates-to-row-${r.id}`}
              data-testid={`responsible-row-${r.id}`}
              className="max-w-[180px]"
            />
          );
        },
      },
      {
        accessorKey: 'group_name' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.group', message: 'Group' }),
        ...widthOf(COLUMN_WIDTH.group),
        editable: false,
        sortable: false,
        cell: ({ row }) => (
          <span className="text-sm truncate max-w-[110px] block" title={row.original.group_name}>
            {row.original.group_name || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'payload' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.severity', message: 'Severity' }),
        id: 'severity',
        ...widthOf(COLUMN_WIDTH.severity),
        editable: false,
        sortable: false,
        cell: ({ row }) => {
          if (row.original.category === 'change_request') {
            const view = parseChangeRequestPayload(row.original.payload);
            const bucket = heldBack.has(row.original.id)
              ? 'needs_handholding'
              : classifyChangeRequestTriage(view, row.original.current_state ?? '');
            const sourceKind = view?.source?.kind;
            // Why "Approve selected / all" leaves this request out.
            const lockReason = isInBatch(row.original, heldBack) ? null : batchLockReason(i18n, row.original, heldBack);
            return (
              <div className="flex items-center gap-1 flex-wrap">
                <StatusBadge
                  tone={bucket === 'needs_handholding' ? 'warning' : 'success'}
                  label={getTriageBucketLabel(i18n, bucket)}
                />
                {lockReason && <BatchLockHint reason={lockReason} />}
                {typeof view?.resolved?.confidence === 'number' && view.resolved.confidence > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {formatConfidence(view.resolved.confidence)}
                  </span>
                )}
                {sourceKind && (
                  <span className="text-xs text-muted-foreground">· {getSourceLabel(i18n, sourceKind)}</span>
                )}
              </div>
            );
          }
          if (row.original.category === 'dunning') {
            const payload = row.original.payload as Record<string, unknown> | undefined;
            const blocker = payload?.blocker as { reason: string } | undefined;
            if (blocker) {
              return <DunningBlockerBadge reason={blocker.reason} />;
            }
            return <span className="text-muted-foreground">—</span>;
          }
          if (row.original.category !== 'accident_notification') return null;
          const payload = row.original.payload as Record<string, unknown> | undefined;
          return <AccidentSeverityBadge severity={payload?.severity as string | undefined} />;
        },
      },
      {
        accessorKey: 'due_at' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.due', message: 'Due' }),
        ...widthOf(COLUMN_WIDTH.due),
        sortable: true,
        editable: false,
        cell: ({ row }) => {
          // A dunning item has no deadline of its own: Due shows the debtor's
          // oldest overdue invoice, the same date as the board chip.
          if (row.original.category === 'dunning') {
            const oldest = row.original.oldest_overdue_date;
            if (!oldest) return <span className="text-sm">—</span>;
            const days = Math.max(0, differenceInCalendarDays(new Date(), parseISO(oldest)));
            const daysLabel = i18n._(
              msg({ id: 'dunning.kanban.daysOverdue', message: '{count, plural, one {# day overdue} other {# days overdue}}' }),
              { count: days },
            );
            return (
              <div className="space-y-0.5" data-testid={`due-row-${row.original.id}`}>
                {days > 0 && <StatusBadge tone="danger" label={daysLabel} title={daysLabel} />}
                <div className="text-xs text-muted-foreground">{formatDateDisplay(oldest, i18n.locale)}</div>
              </div>
            );
          }
          const urgency = getUrgency(row.original.due_at);
          if (!row.original.due_at) return <span className="text-sm">—</span>;
          const label = formatDateTime(row.original.due_at, i18n.locale);
          // The urgency badge and the date sit on two lines so the column stays narrow.
          if (urgency === 'overdue' || urgency === 'urgent') {
            return (
              <div className="space-y-0.5" data-testid={`due-row-${row.original.id}`}>
                <StatusBadge
                  tone={urgency === 'overdue' ? 'danger' : 'warning'}
                  label={
                    urgency === 'overdue'
                      ? i18n._({ id: 'workflow.urgency.overdue', message: 'Overdue' })
                      : i18n._({ id: 'workflow.urgency.urgent', message: 'Urgent' })
                  }
                />
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            );
          }
          return <span className="text-xs text-muted-foreground whitespace-normal">{label}</span>;
        },
      },
      {
        accessorKey: 'created_at' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.created', message: 'Created' }),
        ...widthOf(COLUMN_WIDTH.created),
        sortable: true,
        editable: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {formatDateDisplay(row.original.created_at, i18n.locale)}
            </span>
          ) : (
            <span className="text-sm">—</span>
          ),
      },
      {
        accessorKey: 'current_state' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.state', message: 'State' }),
        ...widthOf(COLUMN_WIDTH.state),
        sortable: false,
        editable: false,
        selectOptions: stateOptions,
        cell: ({ row }) => {
          const state = row.original.current_state;
          const stateLabel = getStateLabel(i18n, state ?? '');
          // The badge truncates inside the narrow column; the title keeps the full label.
          return (
            <div className="max-w-[125px]">
              <StatusBadge tone={getStateTone(state)} label={stateLabel} title={stateLabel} />
            </div>
          );
        },
      },
      {
        accessorKey: 'available_actions' as keyof WorkflowTableRow,
        header: i18n._({ id: 'workflow.column.actions', message: 'Actions' }),
        id: 'actions',
        enableHiding: false,
        editable: false,
        sortable: false,
        ...widthOf(COLUMN_WIDTH.actions),
        cell: ({ row }) => {
          const canHandOver = !row.original.completed_at;
          return (
            <WorkflowRowActionsMenu
              rowId={row.original.id}
              actions={row.original.available_actions ?? []}
              canHandOver={canHandOver}
              canRemind={canHandOver && workflowItemPermissions(row.original, userId, isAdmin).canRemind}
              onHandOver={() => openAssign(row.original)}
              onRemind={() => openRemind(row.original)}
              onAction={(action) => handleActionClick(row.original.id, action, row.original.current_state ?? '')}
            />
          );
        },
      },
      ];
    },
    [
      i18n,
      handleActionClick,
      handleOpenDetail,
      canOpenDetail,
      activeTab,
      heldBack,
      responsibleContext,
      openAssign,
      openRemind,
      userId,
      isAdmin,
      categoryOptions,
      stateOptions,
      responsibleOptions,
    ],
  );

  const pageTitle = i18n._({ id: 'nav.workflows', message: 'Approvals' });

  if (error) {
    return (
      <PageContainer width="list">
        <PageHeader title={pageTitle} />
        <QueryErrorState error={error} onRetry={() => void refetch()} />
      </PageContainer>
    );
  }

  // A search without a hit names the query; the table shows it in place of its
  // generic "No matching results" while a search is active.
  const renderSearchEmptyState = () => {
    const query = search.trim();
    if (!query) return undefined;
    return (
      <EmptyState
        icon={<SearchX className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
        title={i18n._({
          id: 'workflow.empty.search',
          message: 'No results for "{query}"',
          values: { query },
        })}
        description={i18n._({
          id: 'workflow.empty.searchDescription',
          message: 'The search covers the child, the summary and the employee names.',
        })}
        action={
          <Button size="sm" variant="outline" onClick={() => handleSearchChange('')}>
            {i18n._({ id: 'workflow.empty.clearSearch', message: 'Clear search' })}
          </Button>
        }
      />
    );
  };

  // One short line per tab; a narrowed view offers the way back to everything.
  const renderEmptyState = () => {
    const icon = <Inbox className="h-8 w-8 text-muted-foreground" aria-hidden="true" />;
    const narrowed = !!statsFilter || nobodyOnly;
    if (activeTab === 'open' && !narrowed) {
      return (
        <EmptyState
          icon={icon}
          title={i18n._({ id: 'workflow.empty.title', message: 'Nothing waiting' })}
          description={i18n._({
            id: 'workflow.empty.description',
            message: 'New requests from parents and staff appear here.',
          })}
        />
      );
    }
    const titles: Partial<Record<InboxTab, string>> = narrowed
      ? {}
      : {
          mine: i18n._({ id: 'workflow.empty.mine', message: 'Nothing on your list' }),
          overdue: i18n._({ id: 'workflow.empty.overdue', message: 'Nothing overdue' }),
          done: i18n._({ id: 'workflow.empty.done', message: 'Nothing completed yet' }),
        };
    return (
      <EmptyState
        icon={icon}
        title={titles[activeTab] ?? i18n._({ id: 'workflow.empty.scopedTitle', message: 'Nothing in this view' })}
        action={
          <Button size="sm" variant="outline" onClick={handleShowAll}>
            {i18n._({ id: 'workflow.empty.showAll', message: 'Show all items' })}
          </Button>
        }
      />
    );
  };

  const renderTable = (tableItems: WorkflowTableRow[], count: number) => {
    const isScope = activeTab === 'change_requests';
    const isDone = activeTab === 'done';
    // Pending Routine change_request rows eligible for "Approve all" (manual,
    // terminal and held-back items are excluded).
    const batchable = isScope ? tableItems.filter((it) => isInBatch(it, heldBack)) : [];
    // The ticked rows, on every page. Re-gated on each render so a row that
    // was ticked and then reclassified or held back never slips into a batch.
    const ticked = [...selectedIds]
      .map((id) => seenRows.current.get(id))
      .filter((row): row is WorkflowTableRow => !!row);
    const eligible = isScope
      ? ticked.filter((it) => isInBatch(it, heldBack))
      : ticked.filter(isBulkAcknowledgeable);
    const bulkMode: WorkflowBulkMode = isScope ? 'approve' : 'acknowledge';
    // A view loaded in full (fetchAll) is paged here: the server-paged table
    // shows whatever rows it gets, so it must get one page only.
    const pageItems = queryParams.fetchAll ? tableItems.slice(offset, offset + limit) : tableItems;
    // Offered on every open view: the loader reads all pages and keeps only the
    // items that qualify, so a notice on a later page is not missed.
    const showAckAll = !isScope && !isDone;
    const renderSelectionActions = (_ids: string[], clearSelection: () => void) => (
      <WorkflowSelectionActions
        mode={bulkMode}
        selectedCount={selectedIds.size}
        eligibleCount={eligible.length}
        busy={isScope ? batchFlow.busy : ackFlow.busy}
        onRun={() => (isScope ? handleBatchApprove(eligible, true) : handleAcknowledgeSelected(eligible))}
        onClear={clearSelection}
      />
    );
    const toolbarRight = isScope ? (
      <WorkflowBulkAllButton
        mode="approve"
        count={batchable.length}
        disabled={batchable.length === 0 || batchFlow.busy}
        onRun={() => handleBatchApprove(batchable)}
      />
    ) : showAckAll ? (
      <WorkflowBulkAllButton mode="acknowledge" disabled={ackFlow.busy} onRun={handleAcknowledgeAll} />
    ) : undefined;
    return (
    <>
      <BaseEditableTable<WorkflowTableRow>
        // The default column set is read on mount; entering or leaving the
        // change-request list mounts the table with its own set.
        key={isScope ? 'workflow-table-change-requests' : isDone ? 'workflow-table-done' : 'workflow-table'}
        data={pageItems}
        // The standard checkbox column, first, before the details column. The
        // done list has nothing to do in bulk, so it has no checkboxes.
        enableSelection={!isDone}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        renderSelectionActions={renderSelectionActions}
        toolbarRight={toolbarRight}
        columns={columns}
        onCellUpdate={handleCellUpdate}
        isLoading={isLoading}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        defaultPageSize={20}
        enableSearch
        searchPlaceholder={i18n._({
          id: 'workflow.search.placeholder',
          message: 'Search by name or summary...',
        })}
        searchTerm={search}
        onSearchChange={handleSearchChange}
        emptyMessage={i18n._({
          id: 'workflow.empty',
          message: 'No workflow items found',
        })}
        emptyState={renderEmptyState()}
        filteredEmptyState={renderSearchEmptyState()}
        tableId="workflow-inbox"
        defaultColumnVisibility={isScope ? CHANGE_REQUEST_COLUMN_VISIBILITY : DEFAULT_COLUMN_VISIBILITY}
        enableColumnVisibility
        enableFilters
        filterConfig={filterConfig}
        onFilterChange={handleFilterChange}
        defaultColumnFilters={chipFilters}
        serverSidePagination
        totalRows={count}
        pagination={{ pageIndex: Math.floor(offset / limit), pageSize: limit }}
        onPaginationChange={handlePaginationChange}
      />
    </>
    );
  };

  const renderContent = (contentItems: WorkflowTableRow[], count: number) => {
    if (viewMode === 'board') {
      if (isLoading) {
        return (
          <div className="flex items-center justify-center py-10" role="status">
            <Loader2 className="h-6 w-6 animate-spin mr-2" aria-hidden="true" />
            <span className="text-muted-foreground">
              {i18n._({ id: 'workflow.loading', message: 'Loading data...' })}
            </span>
          </div>
        );
      }
      return (
        <WorkflowBoard
          items={contentItems}
          onOpenDetail={handleOpenDetail}
          onActionClick={handleActionClick}
          onBatchApprove={handleBatchApprove}
          batchPending={batchFlow.busy}
          heldBack={heldBack}
          triage={activeTab === 'change_requests'}
          groupBy={boardGrouping === 'person' ? 'responsible' : 'state'}
          responsibleContext={responsibleContext}
        />
      );
    }
    return renderTable(contentItems, count);
  };

  const tabCount = (n: number) => (n > 0 ? n : undefined);
  const isBoard = viewMode === 'board';

  return (
    <>
      {/*
        The board fills the viewport height, like the list view's table does, and
        its columns scroll on their own. The subtracted height is the shell chrome
        (see MessagingPage): top bar 3rem + <main> py-4 2rem + inset m-2 1rem.
        Below md the board grows with its content and the page scrolls. The
        min-height keeps the columns usable on short laptop screens.
      */}
      <PageContainer
        width="list"
        className={cn(isBoard && 'flex flex-col md:h-[calc(100dvh-6rem)] md:min-h-[40rem]')}
      >
        <PageHeader title={pageTitle} className={cn(isBoard && 'flex-none')} />

        <div className={cn('space-y-4', isBoard && 'flex min-h-0 flex-1 flex-col')}>
          <WorkflowStatsStrip
            pendingCount={stats.pendingCount}
            overdueCount={stats.overdueCount}
            dueTodayCount={stats.dueTodayCount}
            myTasksCount={stats.myTasksCount}
            loading={stats.isLoading}
            activeFilter={statsFilter}
            onFilterClick={handleStatsFilterClick}
          />

          {/* Toolbar: scope and who (left) + view (right) */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
            <SegmentedToggle<InboxTab>
              size="sm"
              value={activeTab}
              onValueChange={handleTabChange}
              aria-label={i18n._({ id: 'workflow.tab.ariaLabel', message: 'Show' })}
              options={[
                { value: 'open', label: i18n._({ id: 'workflow.tab.open', message: 'Open' }) },
                {
                  value: 'mine',
                  label: i18n._({ id: 'workflow.tab.mine', message: 'My tasks' }),
                  count: tabCount(stats.myTasksCount),
                },
                {
                  value: 'overdue',
                  label: i18n._({ id: 'workflow.tab.overdue', message: 'Overdue' }),
                  count: tabCount(stats.overdueCount),
                },
                {
                  value: 'change_requests',
                  label: i18n._({ id: 'workflow.tab.changeRequests', message: 'Change requests' }),
                },
                { value: 'done', label: i18n._({ id: 'workflow.tab.done', message: 'Done' }) },
              ]}
            />
            {/* Nobody took the item over, even when a group lead answers for it. */}
            <Button
              size="sm"
              variant={nobodyOnly ? 'default' : 'outline'}
              aria-pressed={nobodyOnly}
              onClick={handleNobodyToggle}
              data-testid="workflow-filter-nobody"
            >
              <Users className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {i18n._({ id: 'workflow.filter.notTaken', message: 'Not taken' })}
            </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {canExportSubsidyReports ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSubsidyExportOpen(true)}
                data-testid="subsidy-report-export-open"
              >
                <Download className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                {i18n._({ id: 'subsidyReport.export.open', message: 'Sozialdepartement export' })}
              </Button>
            ) : null}
            {viewMode === 'board' && activeTab !== 'change_requests' && (
              <SegmentedToggle<BoardGrouping>
                size="sm"
                value={boardGrouping}
                onValueChange={setBoardGrouping}
                aria-label={i18n._({ id: 'workflow.board.groupBy', message: 'Columns' })}
                options={[
                  { value: 'person', label: i18n._({ id: 'workflow.board.byPerson', message: 'By person' }) },
                  { value: 'status', label: i18n._({ id: 'workflow.board.byStatus', message: 'By status' }) },
                ]}
              />
            )}
            <SegmentedToggle<WorkflowViewMode>
              size="sm"
              value={viewMode}
              onValueChange={setViewMode}
              aria-label={i18n._({ id: 'workflow.view.ariaLabel', message: 'View' })}
              options={[
                {
                  value: 'list',
                  label: (
                    <>
                      <List className="h-3.5 w-3.5" aria-hidden="true" />
                      {i18n._({ id: 'workflow.view.listLabel', message: 'List' })}
                    </>
                  ),
                },
                {
                  value: 'board',
                  label: (
                    <>
                      <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />
                      {i18n._({ id: 'workflow.view.boardLabel', message: 'Board' })}
                    </>
                  ),
                },
              ]}
            />
            </div>
          </div>

          <div className={cn('space-y-4', isBoard && 'flex min-h-0 flex-1 flex-col')}>
            {renderContent(filteredItems, totalCount)}
          </div>
        </div>
      </PageContainer>

      {batchFlow.dialog}
      {ackFlow.dialog}

      <SubsidyReportExportDialog open={subsidyExportOpen} onOpenChange={setSubsidyExportOpen} />

      {/* Detail Sheet */}
      <WorkflowDetailSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        item={detailData}
        error={detailError}
        onRetry={() => void refetchDetail()}
        userNameMap={userNameMap}
        onActionClick={handleActionClick}
        onOpenItem={(id) => {
          setSelectedItemId(id);
          setSheetOpen(true);
        }}
        responsibleContext={responsibleContext}
        onHandOver={openAssign}
        onRemind={openRemind}
      />

      {/* Hand-over dialog: mounted once an item was picked, so the close animation runs. */}
      {assignItem && (
        <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} item={assignItem} returnFocusTo={dialogOrigin} />
      )}

      {remindItem && (
        <RemindDialog open={remindOpen} onOpenChange={setRemindOpen} item={remindItem} returnFocusTo={dialogOrigin} />
      )}

      {/* Transition Dialog */}
      {transitionItemId && selectedAction && (
        <TransitionDialog
          open={transitionDialogOpen}
          onOpenChange={setTransitionDialogOpen}
          itemId={transitionItemId}
          action={selectedAction}
          commentRequired={transitionCommentRequired}
          currentState={transitionCurrentState}
          onSuccess={handleTransitionSuccess}
          // The dialog applies both only when the action is `reject`.
          parentNoteEnabled={transitionReasonRequired}
          parentNoteRequired={transitionReasonRequired}
          {...transitionDialogCopy(i18n, selectedAction, transitionCategory)}
          returnFocusTo={dialogOrigin}
        />
      )}

      {/* Shared Dunning Drawer — opened by dunning kanban cards */}
      {dunningDrawer && (
        <DunningWorkflowDrawer
          workflowItemId={dunningDrawer.selectedWorkflowItemId}
          onClose={dunningDrawer.closeDrawer}
        />
      )}
    </>
  );
}

export default WorkflowInboxPage;
